import { useState } from 'react';

import { Box, Stack, Tabs, Text } from '@ekhodealer/ekho-common/rev';

import { SettingsSection } from '@common/components/SettingsSection';
import { SettingsPageContent, type TocItem } from '@common/components/SettingsTableOfContents';

import { CosmeticReportConfig } from './vdp/CosmeticReportConfig';

import './WebsiteSettingsPage.scss';

type WebsiteTab =
	| 'search-results'
	| 'vehicle-details'
	| 'landing-page'
	| 'blog'
	| 'about-us'
	| 'accessories'
	| 'service-scheduling'
	| 'contact';

const TAB_ITEMS: { id: WebsiteTab; content: string }[] = [
	{ id: 'search-results', content: 'Search Results' },
	{ id: 'vehicle-details', content: 'Vehicle Details' },
	{ id: 'landing-page', content: 'Landing Page' },
	{ id: 'blog', content: 'Blog' },
	{ id: 'about-us', content: 'About Us' },
	{ id: 'accessories', content: 'Accessories' },
	{ id: 'service-scheduling', content: 'Service Scheduling' },
	{ id: 'contact', content: 'Contact' },
];

const VEHICLE_DETAILS_TOC: TocItem[] = [
	{ id: 'cosmetic-report', title: 'Cosmetic report' },
];

function PlaceholderContent({ tabName }: { tabName: string }): React.JSX.Element {
	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{tabName}
				</Text>
				<Text size='bodySmall' color='secondary'>
					Configuration coming soon.
				</Text>
			</Stack>
		</Box>
	);
}

function VehicleDetailsTab(): React.JSX.Element {
	return (
		<SettingsPageContent tocItems={VEHICLE_DETAILS_TOC} stickyTop={64}>
			<SettingsSection
				title='Cosmetic report'
				description='Configure the cosmetic report section shown on vehicle detail pages.'
				hideDivider
			>
				<CosmeticReportConfig />
			</SettingsSection>
		</SettingsPageContent>
	);
}

export function WebsiteSettingsPage(): React.JSX.Element {
	const [activeTab, setActiveTab] = useState<WebsiteTab>('search-results');

	const activeTabItem = TAB_ITEMS.find((t) => t.id === activeTab);
	const tabName = activeTabItem?.content ?? 'Search Results';

	return (
		<div className='website-settings'>
			<div className='website-settings__tabs' data-settings-tabs>
				<Tabs
					items={TAB_ITEMS}
					selectedKey={activeTab}
					onSelect={(item) => setActiveTab(item.id as WebsiteTab)}
				/>
			</div>
			<div className='website-settings__content' data-settings-tab-content>
				{activeTab === 'vehicle-details' ? (
					<VehicleDetailsTab />
				) : (
					<PlaceholderContent tabName={tabName} />
				)}
			</div>
		</div>
	);
}
